import { type JSXElement, Show, createEffect, createMemo, createSignal } from 'solid-js'
import './styles.css'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { monthList, viewList, weekDays } from './utils'

import { Button, IconButton, Popover, Text } from '@components'
import { FieldElementProps } from '@modular-forms/solid'
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-solid'
import { css } from 'styled-system/css'
import { Flex, Grid } from 'styled-system/jsx'
import { Input } from '../../ui/input'
import arrowIcon from './arrow.svg'
import calendarLogo from './calendar.svg'
import clockLogo from './clock.svg'

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

interface IPropsValue {
	activeView: string
	startDate?: Date
	endDate?: Date
	currentDate?: Date
	dateRangeDifference: number
	date: string
	month: string
	year: string
	day: string
	time: string
	currentWeekStartDate: Date
	currentWeekEndDate: Date
	setCalendarState: (props: boolean) => void
}

interface ICalendarComponentProps {
	dateFormat?: string
	prevDate?: Date
	minDate?: Date
	maxDate?: Date
	enableDateRangeSelector?: boolean
	currentDate: Date | string | undefined
	headerMonthFormat?: string
	headerYearFormat?: string
	enableArrowNavigation?: boolean
	enableDateInputField?: boolean
	enableDateInputFieldEditor?: boolean
	enableTodayNavigator?: boolean
	activeCalendarView?: 'day' | 'month' | 'year'
	cutomizeCalendarViewButtons?: string
	enableCalendarViewType?: boolean
	enableTimeView?: boolean
	ednableTimeEditing?: boolean
	renameTimeUpdateButton?: string
	closeOnSelect?: boolean
	children?: JSXElement
	onChange?: (value: string) => void
	calendarWidth?: number
}

export const DateTimePicker = (props: ICalendarComponentProps) => {
	props.enableDateRangeSelector = props.enableDateRangeSelector
		? props.enableDateRangeSelector
		: false
	props.prevDate = props.prevDate ? props.prevDate : dayjs().startOf('weeks').toDate()
	props.closeOnSelect = props.closeOnSelect ? props.closeOnSelect : false
	props.calendarWidth = props.calendarWidth ? props.calendarWidth : 0
	props.headerMonthFormat = props.headerMonthFormat ? props.headerMonthFormat : 'MMM'
	props.headerYearFormat = props.headerYearFormat ? props.headerYearFormat : 'YYYY'
	props.enableArrowNavigation = props.enableArrowNavigation
		? props.enableArrowNavigation
		: true
	props.enableDateInputField = props.enableDateInputField
		? props.enableDateInputField
		: false
	props.enableDateInputFieldEditor = props.enableDateInputFieldEditor
		? props.enableDateInputFieldEditor
		: true
	props.dateFormat = props.dateFormat ? props.dateFormat : 'DD MMM YYYY'
	props.enableTodayNavigator = props.enableTodayNavigator
		? props.enableTodayNavigator
		: false
	props.cutomizeCalendarViewButtons = props.cutomizeCalendarViewButtons
		? props.cutomizeCalendarViewButtons
		: ''
	props.activeCalendarView = props.activeCalendarView ? props.activeCalendarView : 'day'
	props.enableCalendarViewType = props.enableCalendarViewType
		? props.enableCalendarViewType
		: false
	props.enableTimeView = props.enableTimeView ? props.enableTimeView : false
	props.ednableTimeEditing = props.ednableTimeEditing ? props.ednableTimeEditing : false
	props.renameTimeUpdateButton = props.renameTimeUpdateButton
		? props.renameTimeUpdateButton
		: ''

	const [locDate, setLocDate] = createSignal<Date | undefined>()

	const [previousDateState, setPreviousDate] = createSignal<Date | undefined>()

	const [dateRangeArr, setdateRangeArr] = createSignal<Date[]>([])

	const [headerView, setHeaderView] = createSignal<{
		[key: string]: number | string
	}>({ monthIndex: 0, month: '', year: 0 })

	const [activeView, setActiveView] = createSignal<string>(props.activeCalendarView)

	const [yearRangeOffset, setYearRangeOffset] = createSignal({
		start: Number(dayjs(props.currentDate).format('YYYY')) - 4,
		offset: 0,
	})

	const [isTimeViewEnabled, setTimeView] = createSignal(false)

	const [timeValue, setTime] = createSignal<{ [key: string]: string }>({
		hour: '',
		min: '',
		meridiem: '',
	})

	const [isCalendarEnabled, setCalendarState] = createSignal(false)

	const dateList = createMemo(() => {
		const currentMonth = headerView().monthIndex
		const currentYear = headerView().year
		const monthStartDate = dayjs(`${currentYear}, ${currentMonth},`)
			.startOf('month')
			.format('DD MMMM, YYYY')

		const weekStartDate = dayjs(monthStartDate).startOf('week').toDate()

		return [...Array(42)].map((_1, index) => {
			return dayjs(weekStartDate).add(index, 'days').toDate()
		})
	})

	const splitArr = (list: Date[], splittedArr: Date[][]) => {
		const subArray: Date[] = []
		const callbackArr: Date[][] = splittedArr

		if (list.length > 7) {
			list.forEach((iterate, index) => {
				if (index !== 0 && index % 7 === 0) {
					callbackArr.push(subArray)
					const val = list.splice(7)
					splitArr(val, callbackArr)
				} else {
					subArray.push(iterate)
				}
			})
		} else if (list.length === 7) {
			callbackArr.push(list)
		}
		return callbackArr
	}

	const dateListss = createMemo(() => {
		const currentMonth = headerView().monthIndex
		const currentYear = headerView().year
		const monthStartDate = dayjs(`${currentYear}-${currentMonth}`)
			.startOf('month')
			.format('DD MMMM, YYYY')

		const weekStartDate = dayjs(monthStartDate).startOf('week').toDate()

		const dateArray = [...Array(42)].map((_1, index) => {
			return dayjs(weekStartDate).add(index, 'days').toDate()
		})

		return splitArr(dateArray, [])
	})

	const momentFormatter = (date: Date | undefined, formatStr: string) => {
		return dayjs(date).format(formatStr)
	}

	// Render only once
	createEffect(() => {
		setPreviousDate(props.prevDate)
		if (props.currentDate && dayjs(props.currentDate).isValid()) {
			setLocDate(dayjs(props.currentDate).toDate())
		} else {
			setLocDate(undefined)
		}
		const startDate = dayjs(props.prevDate).toDate()
		const endDate = dayjs(props.currentDate).toDate()
		if (props.enableDateRangeSelector) {
			setdateRangeArr([startDate, endDate])
		}
	})

	//  Render based on locDate;
	createEffect(() => {
		const curentMeridiem = momentFormatter(locDate(), 'A')
		setTime({
			hour: momentFormatter(locDate(), 'HH'),
			min: momentFormatter(locDate(), 'mm'),
			meridiem: curentMeridiem,
		})
		const month = Number(momentFormatter(locDate(), 'MM'))
		setHeaderView(() => ({
			monthIndex: month,
			month: momentFormatter(locDate(), props.headerMonthFormat || ''),
			year: momentFormatter(locDate(), props.headerYearFormat || ''),
		}))
	})

	createEffect(() => {
		if (props.onChange) {
			props.onChange(locDate()?.toISOString() || '')
		}
	})

	const headerNavigation = (index: number) => {
		const activeYear = Number(headerView().year)
		const activeMonthIndex = Number(headerView().monthIndex)
		const monthVal = new Date(activeYear, activeMonthIndex + index, 0)

		setHeaderView(() => ({
			monthIndex: monthVal.getMonth() + 1,
			month: momentFormatter(monthVal, props.headerMonthFormat || ''),
			year: momentFormatter(monthVal, props.headerYearFormat || ''),
		}))
	}

	// navigate year range during input field onchange and today onClick
	const yearViewNavigation = (value: Date) => {
		if (
			yearRangeOffset().start > value.getFullYear() ||
			yearRangeOffset().start + 8 < value.getFullYear()
		) {
			setYearRangeOffset({ start: value.getFullYear() - 4, offset: 0 })
		}
	}

	// handles onChange in Date edit field
	const editDate = (value: string) => {
		const currentDate: any = dayjs(value).toDate()
		if (currentDate.toString() !== 'Invalid Date') {
			if (!dateRangeArr()[1]) {
				setdateRangeArr([...dateRangeArr(), currentDate])
			}
			setLocDate(currentDate)
			yearViewNavigation(currentDate)
		}
	}

	// handles month view selection
	const monthSelection = (selectedMonthInd: number) => {
		const activeYear = Number(headerView().year)
		const newDate = new Date(activeYear, selectedMonthInd - 1, locDate()?.getDate())
		if (newDate.toString() !== 'Invalid Date') {
			setLocDate(newDate)
		} else {
			setLocDate(new Date(activeYear, selectedMonthInd, 1))
		}
	}

	// handles year view range during navigation
	const yearNavigation = (value: number) => {
		const offset = yearRangeOffset().offset + value
		const startYear = yearRangeOffset().start + 9 * value
		setYearRangeOffset({ start: startYear, offset: offset })
	}

	const handleTimeChange = (value: number, key: string, range: number) => {
		const data = Number(timeValue()[key]) + value
		if (data >= 0 && data <= range) {
			const value = data < 10 ? `0${String(data)}` : data
			setTime({ ...timeValue(), [key]: String(value) })
		}
	}

	const handleMultiSelectDate = (value: Date) => {
		if (dateRangeArr().length === 2) {
			setdateRangeArr([value])
		} else {
			setdateRangeArr([...dateRangeArr(), dayjs(value).endOf('days').toDate()])
		}
	}

	createEffect(() => {
		if (dateRangeArr()[0]) {
			setPreviousDate(dateRangeArr()[0])
		}
		if (dateRangeArr()[1]) {
			setLocDate(dateRangeArr()[1])
		}
	})

	createEffect(() => {
		if (isCalendarEnabled() === false) {
			const newDate = dayjs(locDate()).toDate()
			newDate.setHours(Number(timeValue().hour))
			newDate.setMinutes(Number(timeValue().min))

			if (!dayjs(locDate()).isSame(newDate)) {
				setLocDate(newDate)
				setTimeView(false)
				if (props.closeOnSelect) {
					setCalendarState(false)
				}
			}
		}
	})

	const isTodayEnabled = createMemo(() => {
		const today = dayjs().startOf('days').toDate()
		const seletedDate = dayjs(locDate()).startOf('days').toDate()
		return dayjs(seletedDate).isSame(today)
	})

	const DateView = ({ it }: { it: Date }) => {
		const startDate = dayjs(`${headerView().year}, ${headerView().month}`)
			.startOf('months')
			.toDate()
		const endDate = dayjs(`${headerView().year}, ${headerView().month}`)
			.endOf('months')
			.toDate()

		let isActive = false // gives selected dates
		let isRangeActive = false // highlights the dates in-between
		let isDatesDisabled = false // disables the prev date during selection

		if (props.enableDateRangeSelector) {
			if (dateRangeArr()[0] && !dateRangeArr()[1]) {
				isActive = dayjs(it).isSame(dayjs(previousDateState()).startOf('days'))
				isDatesDisabled = dayjs(it).isBefore(dayjs(previousDateState()).startOf('days'))
				isRangeActive = false
			} else if (dateRangeArr()[0] && dateRangeArr()[1]) {
				isActive =
					dayjs(it).isSame(dayjs(locDate()).startOf('days')) ||
					dayjs(it).isSame(dayjs(previousDateState()).startOf('days'))
				isRangeActive =
					dayjs(it).isAfter(dayjs(previousDateState()).startOf('days')) &&
					dayjs(it).isBefore(dayjs(locDate()).startOf('days'))
			}
		} else {
			isActive = dayjs(it).isSame(dayjs(locDate()).startOf('days'))
		}

		// handles Max date given by user
		if (props.maxDate) {
			isDatesDisabled =
				isDatesDisabled || dayjs(it).isSameOrAfter(dayjs(props.maxDate).startOf('days'))
		}
		if (props.minDate) {
			isDatesDisabled =
				isDatesDisabled ||
				dayjs(it).isSameOrBefore(dayjs(props.minDate).startOf('days'))
		}
		return (
			<div
				class={`week-list-items cur-pointer 
          ${isActive ? `${props.enableDateRangeSelector ? 'active-bg' : 'active '} box-shadow-card` : ''} 
          ${it < startDate || it > endDate ? 'cust-dis' : ''}
          ${isRangeActive ? `bg-hover-clr` : ''}
          ${isDatesDisabled ? 'cust-dis pointer-none' : ''}
          `}
				onClick={() => {
					if (props.enableDateRangeSelector) {
						handleMultiSelectDate(it)
					} else {
						setLocDate(it)
					}
				}}
			>
				{momentFormatter(it, 'DD')}
				{dayjs(it).isSame(dayjs(new Date()).startOf('day')) ? (
					<span class="today_highlight" />
				) : null}
			</div>
		)
	}

	return (
		<Popover.Root
			open={isCalendarEnabled()}
			closeOnInteractOutside={true}
			onOpenChange={(e) => setCalendarState(e.open)}
		>
			<Popover.Anchor>
				<Grid
					gridTemplateColumns="1fr auto"
					gap="1"
				>
					<Input
						type="text"
						value={locDate() ? dayjs(locDate()).format('YYYY-MM-DDTHH:mm:ss') : ''}
						onChange={(e) => {
							const newDate = dayjs(e.target.value)
							if (newDate.isValid()) {
								setLocDate(newDate.toDate())
							} else {
								props.onChange && props.onChange(e.target.value)
							}
						}}
					/>
					<IconButton
						type="button"
						variant="subtle"
						onClick={() => setCalendarState((prev) => !prev)}
					>
						<Calendar />
					</IconButton>
				</Grid>
			</Popover.Anchor>
			<Popover.Positioner>
				<Popover.Content>
					{/* <Popover.Title>Title</Popover.Title>
            <Popover.Description>Description</Popover.Description>*/}
					<Popover.Trigger
						m={1}
						asChild={(props) => (
							<IconButton
								{...props}
								class={css({
									position: 'absolute',
									top: '2',
									right: '2',
								})}
								variant="ghost"
								size="xs"
							>
								<X />
							</IconButton>
						)}
					></Popover.Trigger>

					<div class={`cal-parent ${!isCalendarEnabled() ? 'd-none' : ''}`}>
						{/* Header */}
						<Flex
							justifyContent="center"
							gap={2}
							alignItems="center"
						>
							<Show when={activeView() === 'day'}>
								<IconButton
									variant="ghost"
									type="button"
									// class={`left-arrow ${activeView() === 'day' ? 'cur-pointer' : 'v-none'} ${customizeLeftArrow}`}
									onClick={() => headerNavigation(-1)}
								>
									<ChevronLeft />
								</IconButton>
							</Show>
							<Text>
								{' '}
								{headerView().month} - {headerView().year}{' '}
							</Text>
							<Show when={activeView() === 'day'}>
								<IconButton
									variant="ghost"
									type="button"
									// class={`right-arrow ${activeView() === 'day' ? 'cur-pointer' : 'v-none'} ${customizeRightArrow}`}
									onClick={() => {
										if (activeView() === 'day') headerNavigation(1)
									}}
								>
									<ChevronRight />
								</IconButton>
							</Show>
						</Flex>

						{/* Sub Header */}
						{props.enableTodayNavigator || props.enableDateInputField ? (
							<div
								class={`cal-sub-header ${!props.enableTodayNavigator || !props.enableTimeView ? 'jst-center' : ''}`}
							>
								{props.enableTodayNavigator ? (
									<button
										class={`btn-class jump-today cur-pointer ${isTodayEnabled() ? 'active' : ''}`}
										onClick={() => {
											const newDate = dayjs().toDate()
											yearViewNavigation(newDate)
											setLocDate(newDate)
										}}
									>
										Today
									</button>
								) : null}

								{props.enableDateInputField && (
									<div class="today-col">
										<input
											type="text"
											placeholder="DD MMM YYYY"
											class={`today-col-input`}
											value={momentFormatter(
												locDate() || dayjs().toDate(),
												'DD MMM, YYYY',
											)}
											readOnly={!props.enableDateInputFieldEditor}
											onKeyPress={(event: any) => {
												if (event.key === 'Enter' && event.target.value) {
													editDate(event.target.value)
												}
											}}
										/>
									</div>
								)}
								{props.enableTimeView ? (
									<img
										class={`icon-height cur-pointer`}
										src={isTimeViewEnabled() ? calendarLogo : clockLogo}
										alt="Day Time Icon"
										onClick={() => {
											setTimeView(!isTimeViewEnabled())
										}}
									/>
								) : null}
							</div>
						) : null}

						{/* Calendar View sub-header */}
						<Flex
							justifyContent="center"
							gap={2}
						>
							{viewList.map((it) => {
								return (
									<Button
										type="button"
										size="xs"
										colorPalette={it.value === activeView() ? 'mint' : 'gray'}
										onClick={() => {
											if (isTimeViewEnabled()) {
												setTimeView(false)
											}
											setActiveView(it.value)
										}}
									>
										{it.label}
									</Button>
								)
							})}
						</Flex>

						<div class={`main-container`}>
							{/* Month View */}
							{activeView() !== 'month' || isTimeViewEnabled() ? null : (
								<div class="container-month-view">
									{monthList.map((it, monthIndex) => {
										let isMonthDisabled = false

										if (props.maxDate) {
											if (
												Number(props.maxDate.getFullYear()) <
												Number(locDate()?.getFullYear())
											) {
												isMonthDisabled = true
											} else if (
												Number(props.maxDate.getFullYear()) ===
												Number(locDate()?.getFullYear())
											) {
												isMonthDisabled = Number(props.maxDate.getMonth()) < monthIndex
											}
										}
										if (props.minDate) {
											if (
												Number(props.minDate.getFullYear()) >
												Number(locDate()?.getFullYear())
											) {
												isMonthDisabled = true
											} else if (
												Number(props.minDate.getFullYear()) ===
												Number(locDate()?.getFullYear())
											) {
												isMonthDisabled =
													isMonthDisabled ||
													Number(props.minDate.getMonth()) > monthIndex
											}
										}

										return (
											<div
												class={`container-list cur-pointer ${((locDate()?.getMonth() || 0) + 1) === it.monthIndex ? 'active box-shadow-card' : ''} ${isMonthDisabled ? 'cust-dis pointer-none' : ''}`}
												onClick={() => monthSelection(it.monthIndex)}
											>
												{it.short}
											</div>
										)
									})}
								</div>
							)}

							{/* Year View */}
							{activeView() !== 'year' || isTimeViewEnabled() ? null : (
								<div class="container-year-view">
									<img
										src={arrowIcon}
										class={`year-navi__icon cur-pointer`}
										alt="left arrow"
										onClick={() => {
											yearNavigation(-1)
										}}
									/>

									<div class="container-year-list">
										{[...Array(9)].map((_1, index) => {
											const value = yearRangeOffset().start + index
											const fullYear = Number(momentFormatter(locDate(), 'YYYY'))
											const month = Number(momentFormatter(locDate(), 'MM'))
											const date = Number(momentFormatter(locDate(), 'DD'))
											let isYearDisabled = false
											if (props.maxDate || props.minDate) {
												isYearDisabled =
													(props.maxDate
														? value > Number(props.maxDate.getFullYear())
														: isYearDisabled) ||
													(props.minDate
														? value < Number(props.minDate.getFullYear())
														: isYearDisabled)
											}

											return (
												<div
													class={`container-list cur-pointer ${value === fullYear ? 'active box-shadow-card' : ''} ${isYearDisabled ? 'cust-dis pointer-none' : ''}`}
													onClick={() => {
														setLocDate(new Date(value, month - 1, date))
														if (props.closeOnSelect) {
															setCalendarState(false)
														}
													}}
												>
													{`${value}`}
												</div>
											)
										})}
									</div>
									<img
										src={arrowIcon}
										class={`year-navi__icon year-navi__icon_right cur-pointer`}
										alt="right arrow"
										onClick={() => {
											yearNavigation(1)
										}}
									/>
								</div>
							)}

							{/* Day view */}
							{activeView() !== 'day' || isTimeViewEnabled() ? null : (
								<div class="container-day-view-tab">
									<table>
										<thead>
											{weekDays.map((it) => {
												return (
													<th>
														<div class={`week-list-items pointer-none cust-dis`}>
															{it.short}
														</div>
													</th>
												)
											})}
										</thead>
										<tbody>
											{dateListss().map((iterate, index) => {
												return (
													<tr>
														{iterate.map((it) => {
															return (
																<td>
																	<DateView it={it} />
																</td>
															)
														})}
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
							)}
							{!isTimeViewEnabled() ? null : (
								<div>
									<div class="time-picker">
										<div class={`time-hours-picker`}>
											<img
												src={arrowIcon}
												class={`increment-icon icon_size cur-pointer ${timeValue().hour === '00' ? 'pointer-none cust-dis' : ''}`}
												alt="hour-increment"
												onClick={() => {
													handleTimeChange(-1, 'hour', 23)
												}}
											/>
											<input
												class={`hour_value`}
												value={timeValue().hour}
												type="number"
												readOnly={!props.ednableTimeEditing}
												onKeyPress={(e: any) => {
													if (
														e.target?.value <= 24 &&
														e.target?.value >= 0 &&
														e.code === 'Enter'
													) {
														const value =
															e.target?.value < 10
																? `0${e.target?.value}`
																: e.target?.value
														setTime({ ...timeValue(), hour: value })
													}
												}}
											/>
											<img
												src={arrowIcon}
												class={`decrement-icon icon_size cur-pointer ${timeValue().hour === '23' ? 'pointer-none cust-dis' : ''}`}
												alt="hour-decrement"
												onClick={() => {
													handleTimeChange(1, 'hour', 23)
												}}
											/>
										</div>

										<div class={`time-seperator`}>:</div>
										<div class={`time-mins-picker`}>
											<img
												src={arrowIcon}
												class={`increment-icon icon_size cur-pointer ${timeValue().min === '00' ? 'pointer-none cust-dis' : ''}`}
												alt="min-increment"
												onClick={() => {
													handleTimeChange(-1, 'min', 59)
												}}
											/>
											<input
												class={`min_value`}
												value={timeValue().min}
												type="number"
												readOnly={!props.ednableTimeEditing}
												onKeyPress={(e: any) => {
													if (
														e.target?.value <= 60 &&
														e.target?.value >= 0 &&
														e.code === 'Enter'
													) {
														const value =
															e.target?.value < 10
																? `0${e.target?.value}`
																: e.target?.value
														setTime({ ...timeValue(), min: value })
													}
												}}
											/>
											<img
												src={arrowIcon}
												class={`decrement-icon icon_size cur-pointer ${timeValue().min === '59' ? 'pointer-none cust-dis' : ''}`}
												alt="min-decrement"
												onClick={() => {
													handleTimeChange(1, 'min', 59)
												}}
											/>
										</div>
									</div>
									<Flex
										gap={2}
										alignItems="center"
									>
										<Text>
											{momentFormatter(locDate(), 'ddd DD MMM, YYYY ')}
											{timeValue().hour}:{timeValue().min}
										</Text>
										{/* <div class={`time-view ${customizeConsolidateTimeView}`}>
                        <div class='time-value'>
                          {momentFormatter(locDate(), 'ddd DD MMM, YYYY ')}{timeValue().hour}:{timeValue().min}
                        </div>
                      </div> */}
										{/* <Button
                        type="button"
                        size='xs'
                        onClick={() => {
                          const newDate = dayjs(locDate()).toDate();
                          newDate.setHours(Number(timeValue().hour))
                          newDate.setMinutes(Number(timeValue().min));

                          if (!dayjs(locDate()).isSame(newDate)) {  
                          setLocDate(newDate);
                            setTimeView(false);
                            if (closeOnSelect) {
                              setCalendarState(false);
                            }
                          }
                        }}
                      >
                        {renameTimeUpdateButton || 'Update'}
                      </Button> */}
									</Flex>
								</div>
							)}
						</div>
						{props.children}
					</div>
				</Popover.Content>
			</Popover.Positioner>
		</Popover.Root>
	)
}
